import { Component, computed, inject, input, output, signal } from '@angular/core';
import { CommonModule, DatePipe } from '@angular/common';
import {
  NavButtonComponent,
  I18nService,
  Notification,
  DateFormatSettings,
  UiService,
} from '@lp-ui-lib';
import { NzIconModule } from 'ng-zorro-antd/icon';
import { SensorInstructionComponent } from '../sensor-instruction/sensor-instruction';
import { Router } from '@angular/router';
import { DocumentationWizardComponent } from '../documentation-wizard/documentation-wizard';

@Component({
  selector: 'app-notification-alert',
  standalone: true,
  imports: [
    CommonModule,
    NzIconModule,
    NavButtonComponent,
    DatePipe,
    SensorInstructionComponent,
    DocumentationWizardComponent,
  ],
  templateUrl: './notification-alert.html',
  styleUrls: ['./notification-alert.scss'],
})
export class NotificationAlertComponent {
  // ========= Inputs (Signal Inputs) =========
  notificationList = input<boolean>(true);
  falseAlert = input<boolean>(false);

  /** Recommended: required to avoid passing null to children */
  notification = input.required<Notification>();
  // If you need nullable, switch to:
  // notification = input<Notification | null>(null);

  dateFormat = input<DateFormatSettings | null>(null);

  // ========= Outputs =========
  assetNameClick = output<void>();
  documentationAddedOut = output<void>();
  repairAndReplaceClick = output<void>();

  // ========= Services =========
  private router = inject(Router);
  public i18n = inject(I18nService);
  public ui = inject(UiService);

  // ========= Local UI State (Signals) =========
  overlayDocumentation = signal(false);
  overlayInstructions = signal(false);
  documentationAdded = signal(false);

  // Keep current 1st URL segment (non-reactive to route changes unless you want a subscription)
  private urlSig = signal<string>('');

  constructor() {
    // Initialize the url segment once; can be extended to listen to Router events if needed
    const seg = (this.router.url || '').split('/')[1] ?? '';
    this.urlSig.set(seg);
  }

  // ========= Derived (Computed) =========
  Critical = computed<boolean>(() => this.notification().isCritical);
  Warning = computed<boolean>(() => this.notification().isWarning);
  InActive = computed<boolean>(() => this.notification().inactive);
  Working = computed<boolean>(() => !this.InActive() && !this.Critical() && !this.Warning());

  /** True when not on /notifications and equipment is rotating */
  isRotate = computed<boolean>(() => {
    const url = this.urlSig();
    const n = this.notification();
    if (url !== 'notifications') {
      return n.equipmentType === 'Rotating Equipment';
    }
    return false;
  });

  /** Support both method-based and property-based UiService APIs */
  isDesktop = computed<boolean>(() =>
    typeof this.ui.IsDesktop === 'function' ? this.ui.IsDesktop() : !!(this.ui as any).IsDesktop
  );
  isMobile = computed<boolean>(() =>
    typeof this.ui.IsMobile === 'function' ? this.ui.IsMobile() : !!(this.ui as any).IsMobile
  );
  isTablet = computed<boolean>(() =>
    typeof this.ui.IsTablet === 'function' ? this.ui.IsTablet() : !!(this.ui as any).IsTablet
  );

  /** 'MM/dd/yyyy | h:mm a' vs 'dd-MM-yyyy | HH:mm' */
  dateFormatString = computed<string>(() => {
    const df = this.dateFormat(); // invoke InputSignal
    return df?.formatString === 'MM/dd/yyyy' ? 'MM/dd/yyyy | h:mm a' : 'dd-MM-yyyy | HH:mm';
  });

  /** 12.34 unit */
  Value = computed<string>(() => {
    const n = this.notification();
    if (n.value === null || n.value === undefined || isNaN(Number(n.value))) return '';
    return `${this.formatNumber(n.value)} ${this.getUnit(n.measuredValue)}`.trim();
  });

  /** (current - prev).toFixed with unit */
  ValueIncrease = computed<string>(() => {
    const n = this.notification();
    if (
      n.value === null ||
      n.value === undefined ||
      isNaN(Number(n.value)) ||
      n.prevValue === null ||
      n.prevValue === undefined ||
      isNaN(Number(n.prevValue))
    ) {
      return '';
    }
    const delta = Number(n.value) - Number(n.prevValue);
    return `${this.formatNumber(delta)} ${this.getUnit(n.measuredValue)}`.trim();
  });

  /** Message with placeholders replaced by objectName */
  Message = computed<string>(() => {
    const parsed = this.i18n.parse(this.notification().message) ?? '';
    return this.enrichMessage(parsed);
  });

  /** Instructions with placeholders replaced by objectName */
  Instructions = computed<string>(() => {
    const parsed = this.i18n.parse(this.notification().instructions) ?? '';
    return this.enrichMessage(parsed);
  });

  // ========= Methods (unchanged behavior) =========
  private enrichMessage(message: string): string {
    const n = this.notification();
    return (
      message?.replace('{ObjectName}', n.objectName)?.replace('{Objektname}', n.objectName) ??
      message
    );
  }

  /**
   * Formats a number similar to your original util.
   * Keeps precision handling and epsilon guard.
   */
  private formatNumber(num: any, precision = 3): string {
    const val = Number(num);
    if (!isFinite(val)) return '';
    const epsilon = 1e-11;
    if (Math.abs(val) < epsilon) return '0';
    if (Math.abs(val) < Math.pow(10, -precision)) {
      if (val > 0) {
        return String(Math.ceil(val * Math.pow(10, precision)) / Math.pow(10, precision));
      } else {
        return String(Math.floor(val * Math.pow(10, precision)) / Math.pow(10, precision));
      }
    } else {
      return String(parseInt(String(val * Math.pow(10, precision)), 10) / Math.pow(10, precision));
    }
  }

  /** Extracts the trailing unit from a "measuredValue" string */
  private getUnit(val: any): string {
    const s = String(val ?? '');
    const parts = s.replace(/[0-9_.,#]/g, '').split(' ');
    if (parts.length === 0) return '';
    return parts[parts.length - 1];
  }

  // ========= Events =========
  onDocumentationAdded(): void {
    this.documentationAdded.set(true);
    this.documentationAddedOut.emit();
  }

  onRepairAndReplace(): void {
    const n = this.notification();
    this.repairAndReplaceClick.emit();
    this.router.navigate(['repair', n.objectID, n.sensorID]);
  }

  onAssetName(): void {
    const n = this.notification();
    this.assetNameClick.emit();
    this.router.navigate(['asset', n.objectID, n.sensorID]);
  }
}
